// Core
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Image, Divider, Button, Grid, Text, Flex, Skeleton, useColorMode, useColorModeValue } from '@chakra-ui/react';
import { NavLink, Link, useHistory } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import { getLinkPetrolMembership, getInitials } from 'services/Utils';
import { ReactComponent as StationIcon } from 'assets/svg/station.svg';
import { ReactComponent as LeaderboardIcon } from 'assets/svg/leaderboard.svg';
import stationBgImage from 'assets/img/station-bg.jpg';
import bgGreenImage from 'assets/img/petrol-bg-green.jpg';
import bgPurpleImage from 'assets/img/petrol-bg-purple.jpg';
import petrolLogoImage from 'assets/img/petrol-logo.png';
import leadersImage from 'assets/img/petrol-leaders.png';
import petrolMoneyImage from 'assets/img/petrol-money.png';
import petrolGasImage from 'assets/img/petrol-gas.png';
import petrolBlockMoreCharacterImage from 'assets/img/petrol-block-find-out-more/character.png';
import petrolBlockMoreCoffeeImage from 'assets/img/petrol-block-find-out-more/coffee.png';
import petrolBlockMoreCoinsImage from 'assets/img/petrol-block-find-out-more/coins.png';
import petrolBlockMoreGasImage from 'assets/img/petrol-block-find-out-more/gas.png';
import petrolBlockMoreMoney1Image from 'assets/img/petrol-block-find-out-more/money-1.png';
import petrolBlockMoreMoney2Image from 'assets/img/petrol-block-find-out-more/money-2.png';
import petrolBlockMorePhoneImage from 'assets/img/petrol-block-find-out-more/phone.png';

// Components
import Card from 'components/Card/Card';

const PetrolStation = () => {
	const history = useHistory();

	const { colorMode } = useColorMode();
  	const isDark = colorMode === 'dark';

	const textColor = useColorModeValue('#0E1633', 'white');
	const colorL = useColorModeValue('#9FABCB', '#9FABCB');
	const gradientPricesLeft = useColorModeValue('linear-gradient(-90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)', 'linear-gradient(-90deg, rgba(14, 22, 51, 0.00) 0%, #0E1633 100%)');
	const gradientPricesRight = useColorModeValue('linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)', 'linear-gradient(90deg, rgba(14, 22, 51, 0.00) 0%, #0E1633 100%)');
	const borderLeaderboards = useColorModeValue('1px solid #E4E7EC', '1px solid #262D47');
	const bgLeaderboardsItem1 = useColorModeValue('linear-gradient(130deg, #ECB118 12.54%, #E18937 77.36%)', 'linear-gradient(130deg, #ECB118 12.54%, #E18937 77.36%)');
	const bgLeaderboardsItem2 = useColorModeValue('#FFF', '#0E1633');
	const bgLeaderboardsItem3 = useColorModeValue('#F1F5F9', '#141C3B');
	const bgLeaderboardsAmount = useColorModeValue('white', '#0E1633');
	const stationBgGradient = useColorModeValue('linear-gradient(93deg, #ffffffde 90%, white 100%)', 'linear-gradient(93deg, #0e1633f2 90%, rgb(27 35 62) 100%)');

	const getBgLeaderboard = (index) => {
	  switch (index) {
	    case 0: return bgLeaderboardsItem1;
	    case 1: return bgLeaderboardsItem2;
	    case 2: return bgLeaderboardsItem3;
	    default: return bgLeaderboardsItem2;
	  }
	};

	const getBorderLeaderboard = (index) => {
	  switch (index) {
	    case 0: return '12px';
	    case 1: return '12px 12px 0 0';
	    case 2: return '0 0 12px 12px';
	    default: return '12px';
	  }
	};
	
	const { data: dataPrices, isFetching: isFetchingPrices } = useQuery({
	    queryKey: ['petrol-prices'],
	    queryFn: async () => {
	        try {
		      const response = await apiService.getPetrolPrices();
		      return Array.isArray(response?.data) ? response.data : [];
		    } catch (e) {
		      return [];
		    }
	    },
	    refetchOnWindowFocus: false,
	    retry: false
	});

	const { data: dataLeaderboard, isFetching: isFetchingLeaderboard } = useQuery({
	    queryKey: ['petrol-leaderboard'],
	    queryFn: async () => {
	        try {
		      const response = await apiService.getPetrolLeaderboards({range: 'year'});
		      return Array.isArray(response?.data)
		        ? response.data.slice(0, 3)
		        : [];
		    } catch (e) {
		      return [];
		    }
	    },
	    refetchOnWindowFocus: false,
	    retry: false
	});

	const handleFindOutMore = (e) => {
	  history.push('/download');
	};

    return (
    	<Box mt={{base: '0', md: '44px'}} pb="24px">
	        <Text as="h4" variant="header" fontSize={{base: '24px', md: '32px'}} fontStyle="normal" mb={{base: '24px', sm: '32px'}}>LMCT+ Petrol Loyalty</Text>

	        {isFetchingPrices ? (
			  <Skeleton h="85px" borderRadius="16px" />
			) : dataPrices?.length > 0 ? (
			  <Card padding={{base: '16px 0px', md: '24px 0px'}} overflow="hidden" position="relative">
			  	<Box bg={gradientPricesLeft} position="absolute" left="0" top="0" w="30px" h="100%" zIndex="2" />
			    <Box
			      whiteSpace="nowrap"
			      overflow="hidden"
			      position="relative"
			    >
			      <Flex
			        width="fit-content"
			        gap="24px"
			        alignItems="center"
			        sx={{
			          animation: 'ticker 20s linear infinite',
			          '@keyframes ticker': {
			            '0%': {
			              transform: 'translateX(0)',
			            },
			            '100%': {
			              transform: 'translateX(-50%)',
			            },
			          },
			        }}
			      >
			        {[...dataPrices, ...dataPrices, ...dataPrices].map((row, index) => (
			          <Flex
			            key={`${row?.short}-${index}`}
			            gap="8px"
			            alignItems="center"
			            flexShrink={0}
			          >
			            <Box
			              h={{ base: '24px', md: '37px' }}
			              p={{base: '1px 12px', md: '4px 12px'}}
			              borderRadius="8px"
			              bg={`#${row?.color}`}
			            >
			              <Text
			                variant="title-lg"
			                fontWeight="900"
			                fontSize={{ base: '18px', md: '24px' }}
			                color="white"
			              >
			                {row?.short}
			              </Text>
			            </Box>

			            <Flex gap="4px" alignItems="center">
			              <Text
			                fontSize={{ base: '18px', md: '24px' }}
			                fontWeight="900"
			              >
			                {row?.price}¢
			              </Text>

			              <Text
			                fontSize={{ base: '13px', md: '20px' }}
			                fontWeight="700"
			                color={colorL}
			              >
			                /L
			              </Text>
			            </Flex>
			          </Flex>
			        ))}
			      </Flex>
			    </Box>
			    <Box bg={gradientPricesRight} position="absolute" right="0" top="0" w="30px" h="100%" zIndex="2" />
			  </Card>
			) : <></>}

	        <Card mt="16px" padding="24px 16px" bg={`url(${stationBgImage})`} 
	        	overflow="hidden" 
	        	position="relative"
	        	className="animation-petrol-station-block"
	        	_before={{
	        		content: '""',
	        		position: 'absolute',
	        		width: '100%',
	        		height: '100%',
	        		top: 0,
	        		left: 0,
	        		background: stationBgGradient
	        	}}
	        	>
	        	<Flex gap="12px" zIndex="9">
	        		<StationIcon />
	        		<Text variant="header-gradient" fontStyle="normal" fontSize={{base: '36px', md: '44px', lg: '48px'}}>Save on Fuel at LMCT+</Text>
	        	</Flex>
	        	<Text variant="title-lg" fontWeight="900" textTransform="uppercase" 
	        		fontSize={{base: '36px', md: '44px', lg: '48px'}}
	        		mt="2px"
	        		zIndex="9"
	        		letterSpacing="-1px"
	        		>Petrol Stations</Text>

	        	<Text as="h2" maxW={{base: '100%', md: '38%'}} color={textColor} 
	        		p={{base: '12px 0', md: '18px 0 39px 0'}}
	        		zIndex="9"
	        		textShadow={{base: 'unset', md: isDark ? '1px 1px #3d3d3d' : 'unset', lg: 'unset'}}
	        		>
		        	Save on fuel with LMCT+ fuel stations. Get wholesale pricing at a daily fixed rate, unlock discounts, earn rewards, and climb the leaderboard with Entry, Premium, Elite, or Fuel Saver memberships.
		        </Text>

		        <Button 
		        	variant={ isDark ? 'button-lg-g' : 'button-lg' }
		        	zIndex="2"
		        	onClick={handleFindOutMore}
		        	zIndex="9"
		        	w={{base: '100%', md: '240px'}}
		        	mt={{base: '12px', md: '0px'}}
		        	mb={{base: '243px', md: '4px'}}
		        >Find out more</Button>

		        <Box position="absolute" top="0" left="0px" w="100%" h="100%"> 
					<Image className="animation-petrol-station-block-item-1" src={petrolBlockMoreCharacterImage} alt="Character" zIndex="1" objectFit="contain" w={{base: '170px', md: '260px'}} h="370px" position="absolute" right={{base: '-19px', md: '-45px'}} bottom={{base: '-130px', md: '-144px'}} transform="rotate(0deg)" zIndex="7" />
					<Image className="animation-petrol-station-block-item-2" src={petrolBlockMoreCoffeeImage} alt="Coffee" zIndex="1" objectFit="contain" w={{base: '70px', md: '120px'}} h="180px" position="absolute" right={{base: '233px', md: '313px'}} bottom={{base: '-59px', md: '-90px'}} transform="rotate(14deg)" zIndex="3" />
					<Image className="animation-petrol-station-block-item-3" src={petrolBlockMoreCoffeeImage} alt="Coffee" zIndex="1" objectFit="contain" w={{base: '60px', md: '100px'}} h="170px" position="absolute" right={{base: '276px', md: '386px'}} bottom={{base: '-57px', md: '-90px'}} transform="rotate(-16deg)" zIndex="3" />
					<Image className="animation-petrol-station-block-item-4" src={petrolBlockMoreGasImage} alt="Gas" zIndex="1" objectFit="contain" w={{base: '230px', md: '350px'}} h="100%" position="absolute" right={{base: '197px', md: '277px'}} bottom={{base: '-220px', md: '-60px'}} transform="rotate(0deg)" zIndex="2" />
					<Image className="animation-petrol-station-block-item-5" src={petrolBlockMoreMoney1Image} alt="Money" zIndex="1" objectFit="contain" w={{base: '100px', md: '200px'}} h="100%" position="absolute" right="256px" bottom={{base: '-218px', md: '-138px'}} transform="rotate(0deg)" />
					<Image className="animation-petrol-station-block-item-6" src={petrolBlockMoreMoney2Image} alt="Money" zIndex="1" objectFit="contain" w={{base: '170px', md: '280px'}} h="100%" position="absolute" right="184px" bottom={{base: '-171px', md: '-31px'}} transform="rotate(0deg)" />
					<Image className="animation-petrol-station-block-item-7" src={petrolBlockMoreMoney2Image} alt="Money" zIndex="1" objectFit="contain" w={{base: '200px', md: '330px'}} h="100%" position="absolute" right="-70px" bottom={{base: '-190px', md: '-60px'}} transform="rotate(0deg)" />
					<Image className="animation-petrol-station-block-item-8" src={petrolBlockMorePhoneImage} alt="Petrol App" zIndex="1" objectFit="contain" w={{base: '225px', md: '375px'}} h="400px" position="absolute" right="43px" bottom={{base: '-84px', md: '-53px'}} transform="rotate(0deg)" zIndex="5" />
					<Image className="animation-petrol-station-block-item-9" src={petrolBlockMoreCoinsImage} alt="Coins" zIndex="1" objectFit="contain" w={{base: '118px', md: '160px'}} h="280px" position="absolute" right="18px" bottom={{base: '-111px', md: '-128px'}} transform="rotate(0deg)" zIndex="6" />
				</Box>
    		</Card>

    		<Grid templateColumns={{ sm: '1fr', md: '1fr 1fr' }} 
    			gap="16px" 
    			mt="16px">
    			<Card padding="20px 16px" bg={`url(${bgGreenImage})`} backgroundPosition="center" backgroundSize="cover" overflow="hidden" className="animation-petrol-saving-block">
    				<Text variant="title-lg" color="white" fontSize={{base: '24px', md: '32px'}} fontWeight="900" letterSpacing="-1px" textTransform="uppercase">
    					Savings League
    				</Text>
    				<Box w={{base: '100%', md: '250px'}} mb={{base: '88px', md: '0'}}>
    					{ isFetchingLeaderboard ? 
    						<Box mt="16px" zIndex="9" position="relative">
    							<Skeleton h="52px" borderRadius="12px" />
    							<Skeleton mt="8px" h="104px" borderRadius="12px" />
    						</Box>
    					 : dataLeaderboard?.length > 0 ? 
    					 	<Box mt="16px" zIndex="9" position="relative" w="100%">
    					 	{ dataLeaderboard?.map((row, index) => (
    					 		<Flex 
    					 			key={index} 
    					 			h="52px"
    					 			w="100%"
    					 			justifyContent="space-between"
    					 			alignItems="center"
    					 			borderRadius={() => getBorderLeaderboard(index)}
    					 			p="10px 8px" 
    					 			mt={index === 1 && '8px'}
    					 			gap="0px"
    					 			bg={() => getBgLeaderboard(index)}
    					 			border={borderLeaderboards}>
    					 			<Flex gap="6px" alignItems="center" flex="1" minW="0">
									  {/*{row?.image?.length > 3 ? (
									    <Image
									      border={borderLeaderboards}
									      src={row?.image}
									      w="38px"
									      h="38px"
									      minW="38px"
									      objectFit="cover"
									      borderRadius="full"
									    />
									  ) : (
									    <Box
									      border={borderLeaderboards}
									      className={
									        row?.gender === 'Male'
									          ? 'gender-male'
									          : row?.gender === 'Female'
									          ? 'gender-female'
									          : row?.gender === 'Transgender'
									          ? 'gender-other'
									          : 'gender-none'
									      }
									      w="38px"
									      minW="38px"
									      h="38px"
									      borderRadius="full !important"
									    >
									      <Text pt="9px !important">
									        {getInitials(row?.firstName, row?.lastName)}
									      </Text>
									    </Box>
									  )}*/}

									  <Text
									    variant="title-md"
									    color={index === 0 && 'white'}
									    minW="8px"
									  >
									    {index + 1}
									  </Text>

									  <Text
									    isTruncated
									    flex="1"
									    minW="0"
									    variant="title-md"
									    color={index === 0 && 'white'}
									    pr="8px"
									  >
									  	{!row?.firstName && !row?.lastName ? 'Unknown' : 
										  	<>
											    {row?.firstName}{' '}
											    {row?.lastName ? `${row?.lastName?.charAt(0)}.` : ''}
										    </>
										}
									  </Text>
									</Flex>
    					 			<Box
    					 				borderRadius="16px"
										border={borderLeaderboards}
										bg={bgLeaderboardsAmount}
										p="2px 8px"
    					 				>
    					 				<Text variant="title-md">${Math.round(row?.amount)}</Text>
    					 			</Box>
    					 		</Flex>
    					 	))}
    						</Box>
    						: 
    						<Box 
    							mt="16px" 
    							p="48px 24px"
    							zIndex="9" 
    							borderRadius="12px"
    							position="relative"
    							bg={() => getBgLeaderboard(1)}
    					 		border={borderLeaderboards} >
    							<LeaderboardIcon style={{margin: '0 auto'}} />
    							<Text variant="title-lg" m="4px auto 0 auto" textAlign="center">Leaderboard is empty</Text>
    						</Box>
    					}

    				</Box>

    				<Box position="absolute" top="0" left="0px" w="100%" h="100%"> 
    					<Image className="animation-petrol-saving-block-item-1" alt="leaderboard" src={leadersImage} objectFit="contain" w={{base: '170px', md: '170px'}} h="238px" position="absolute" right={{base: '-68px', md: '-31px'}} bottom={{base: '-108px', md: '-6px'}} />
    					<Image className="animation-petrol-saving-block-item-2" alt="Money" src={petrolMoneyImage} objectFit="contain" w={{base: '200px', md: '230px'}} h="235px" position="absolute" right={{base: '9px', md: '-49px'}} bottom={{base: '-125px', md: '-83px'}} zIndex="2"/>
    					<Image className="animation-petrol-saving-block-item-3" alt="Petrol Station" src={petrolGasImage} objectFit="contain" w={{base: '210px', md: '210px'}} h="200px" position="absolute" right={{base: '137px', md: '57px'}} bottom={{base: '-81px', md: '-31px'}} />
    				</Box>
    			</Card>

    			<Card padding="20px 16px" bg={`url(${bgPurpleImage})`} backgroundPosition="center" backgroundSize="cover" overflow="hidden" className="animation-petrol-membership-block">
    				<Text variant="title-lg" 
    					zIndex="2"
    					w={{base: '100%', md: '66%'}}
    					textShadow={{base: 'unset', md: '1px 1px #3d3d3d', lg: 'unset'}}
    					color="white" fontSize={{base: '24px', md: '32px'}} fontWeight="900" letterSpacing="-1px" textTransform="uppercase">
    					Petrol Saver Membership
    				</Text>

					<Text variant="text-md" color="white" py="16px" w={{base: '100%', md: '300px'}} zIndex="2" textShadow={{base: 'unset', md: '1px 1px #3d3d3d', lg: 'unset'}}>
						Fuel Saver - Basic access with fuel discounts and entry rewards.
					</Text>
				
					<Button zIndex="3" w={{base: '100%', md: '250px'}} mb={{base: '140px', md: '0'}} onClick={() => window.location.href = getLinkPetrolMembership()} mt="16px" variant="secondary-lg">Buy now</Button>

					<Box position="absolute" top="0" left="0px" w="100%" h="100%"> 
    					<Image className="animation-petrol-membership-block-item-1" alt="Petrol Saver" src={petrolLogoImage} zIndex="1" objectFit="contain" w={{base: '230px', md: '270px'}} h="280px" position="absolute" right={{base: '60px', md: '-15px'}} bottom={{base: '-101px', md: '-40px'}} transform="rotate(7deg)" />
    					<Image className="animation-petrol-membership-block-item-1" alt="Petrol Saver" src={petrolLogoImage} zIndex="0" filter="blur(4px)" objectFit="contain" w="110px" h="110px" position="absolute" right={{base: '-11px', md: '19px'}} top={{base: 'unset', md: '-20px'}} bottom={{base: '-10px', md: 'unset'}} transform="rotate(30deg)" />
    					<Image className="animation-petrol-membership-block-item-1" alt="Petrol Saver" src={petrolLogoImage} zIndex="0" filter="blur(4px)" objectFit="contain" w="110px" h="110px" position="absolute" right={{base: '239px', md: '179px'}} bottom={{base: '-16px', md: '-46px'}} transform="rotate(-15deg)" />
    				</Box>
    			</Card>
    		</Grid>
        </Box>
    );
};

export default PetrolStation;